import React, { useState } from "react";
import {
  FileSpreadsheet,
  Calendar,
  FileText,
  Layers,
  FileCheck2,
  Check,
  Package,
} from "lucide-react";
import { Modal } from "../common/Modal";
import {
  Opportunity,
  Requirement,
  ProposalMilestone,
  ProposalAddendum,
  RequiredForm,
  FormatRule,
  ProposalSectionGroup,
  StrategicAssessment,
} from "../../types";
import {
  exportRequirementsMatrixCsv,
  exportResponseScheduleCsv,
  exportSectionAllocationCsv,
  exportFormsAndRulesCsv,
  generateExecutiveBriefHtml,
  generateExecutiveBriefMarkdown,
  downloadTextFile,
} from "../../services/planningExportService";
import { buildIcs } from "../../utils/calendarUtils";

interface ExportPlanningModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  assessment?: StrategicAssessment | null;
  requirements: Requirement[];
  milestones: ProposalMilestone[];
  addenda: ProposalAddendum[];
  forms: RequiredForm[];
  formatRules: FormatRule[];
  sectionGroups: ProposalSectionGroup[];
}

export const ExportPlanningModal: React.FC<ExportPlanningModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  assessment,
  requirements,
  milestones,
  addenda,
  forms,
  formatRules,
  sectionGroups,
}) => {
  const [downloadedItem, setDownloadedItem] = useState<string | null>(null);

  const cleanTitle = (opportunity.title || "Proposal").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 30);
  const prefix = `MLC_${cleanTitle}`;

  const triggerDownloadNotification = (label: string) => {
    setDownloadedItem(label);
    setTimeout(() => {
      setDownloadedItem(null);
    }, 2500);
  };

  const handleExportMatrix = () => {
    const csv = exportRequirementsMatrixCsv(opportunity, requirements);
    downloadTextFile(`${prefix}_Requirements_Matrix.csv`, csv);
    triggerDownloadNotification("Requirements Matrix CSV");
  };

  const handleExportSchedule = () => {
    const csv = exportResponseScheduleCsv(opportunity, milestones);
    downloadTextFile(`${prefix}_Response_Schedule.csv`, csv);
    triggerDownloadNotification("Response Schedule CSV");
  };

  const handleExportIcs = () => {
    const ics = buildIcs(milestones, opportunity.title, opportunity.client);
    downloadTextFile(`${prefix}_Milestones.ics`, ics, "text/calendar;charset=utf-8;");
    triggerDownloadNotification("Calendar (.ICS) File");
  };

  const handleExportSections = () => {
    const csv = exportSectionAllocationCsv(opportunity, sectionGroups);
    downloadTextFile(`${prefix}_Section_Allocation.csv`, csv);
    triggerDownloadNotification("Section Ownership CSV");
  };

  const handleExportForms = () => {
    const csv = exportFormsAndRulesCsv(opportunity, forms, formatRules);
    downloadTextFile(`${prefix}_Forms_and_Rules.csv`, csv);
    triggerDownloadNotification("Forms & Rules CSV");
  };

  const handleExportBriefHtml = () => {
    const html = generateExecutiveBriefHtml({
      opportunity,
      assessment,
      requirements,
      milestones,
      addenda,
      forms,
      formatRules,
      sectionGroups,
    });
    downloadTextFile(`${prefix}_Executive_Brief.html`, html, "text/html;charset=utf-8;");
    triggerDownloadNotification("Executive Brief HTML");
  };

  const handleExportBriefMarkdown = () => {
    const md = generateExecutiveBriefMarkdown({
      opportunity,
      assessment,
      requirements,
      milestones,
      addenda,
      forms,
      formatRules,
      sectionGroups,
    });
    downloadTextFile(`${prefix}_Executive_Brief.md`, md, "text/markdown;charset=utf-8;");
    triggerDownloadNotification("Executive Brief Markdown");
  };

  const handleExportAll = () => {
    handleExportMatrix();
    setTimeout(() => handleExportSchedule(), 200);
    setTimeout(() => handleExportSections(), 400);
    setTimeout(() => handleExportForms(), 600);
    setTimeout(() => handleExportBriefHtml(), 800);
    triggerDownloadNotification("Complete Planning Package");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Proposal Planning Package" maxWidth="xl">
      <div className="space-y-4">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Download planning data tables and executive summaries for kickoff meetings, offline
          collaboration, and client compliance tracking.
        </p>

        {downloadedItem && (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
            <Check className="h-4 w-4" />
            <span>Downloaded: {downloadedItem}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {/* Matrix CSV */}
          <button
            type="button"
            onClick={handleExportMatrix}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Requirements Matrix
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                CSV of all {requirements.length} requirements with prompts & assignees.
              </div>
            </div>
          </button>

          {/* Schedule CSV */}
          <button
            type="button"
            onClick={handleExportSchedule}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-950/60 dark:text-purple-300">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Response Schedule
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                CSV of all {milestones.length} milestones, gates, and due dates.
              </div>
            </div>
          </button>

          {/* Section Outline CSV */}
          <button
            type="button"
            onClick={handleExportSections}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Section Allocation Matrix
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                CSV of {sectionGroups.length} work packages, drafters & word budgets.
              </div>
            </div>
          </button>

          {/* Forms & Rules CSV */}
          <button
            type="button"
            onClick={handleExportForms}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300">
              <FileCheck2 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Forms & Format Rules
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                CSV of {forms.length} forms and {formatRules.length} formatting rules.
              </div>
            </div>
          </button>

          {/* Calendar ICS */}
          <button
            type="button"
            onClick={handleExportIcs}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Calendar Feed (.ICS)
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Sync milestones with Apple, Google, or Outlook Calendar.
              </div>
            </div>
          </button>

          {/* Executive Brief HTML */}
          <button
            type="button"
            onClick={handleExportBriefHtml}
            className="btn-animate flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-[#2ba5b5] hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-[#2ba5b5] dark:hover:bg-slate-800/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-950/60 dark:text-teal-300">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-white">
                Executive Brief (HTML)
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Formatted HTML file ready to view, print, or attach.
              </div>
            </div>
          </button>
        </div>

        {/* Complete Package Button */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handleExportAll}
            className="btn-animate flex w-full items-center justify-center gap-2 rounded-xl bg-[#2ba5b5] py-2.5 text-xs font-bold text-white shadow-sm hover:bg-[#238a97]"
          >
            <Package className="h-4 w-4" />
            <span>Download Complete Planning Bundle (All 5 Files)</span>
          </button>
        </div>

        <div className="flex justify-between border-t border-slate-200 pt-3 dark:border-slate-800">
          <button
            type="button"
            onClick={handleExportBriefMarkdown}
            className="text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            Download Brief as Markdown (.md)
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
};
